import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar, Clock, User, Tag, ArrowLeft, Search, Eye } from "lucide-react";
import PageHero from "../components/PageHero";
import SEOHead from "../components/SEOHead";
import api from "../lib/api";

const categoryColors = {
  "Career Guide": "bg-blue-100 text-blue-800",
  "Industry Trends": "bg-purple-100 text-purple-800",
  "Student Stories": "bg-green-100 text-green-800",
  "Course Guide": "bg-yellow-100 text-yellow-800",
  "General": "bg-slate-100 text-slate-800",
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/blog", { params: { published_only: true } }),
      api.get("/blog/categories"),
    ])
      .then(([p, c]) => {
        setPosts(p.data);
        setCategories(c.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = posts.filter((p) => {
    if (activeCat !== "all" && p.category !== activeCat) return false;
    if (query && !`${p.title} ${p.excerpt} ${(p.tags || []).join(" ")}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const featured = posts.filter((p) => p.featured).slice(0, 1)[0];

  return (
    <>
      <PageHero
        kicker="Kiki Blog"
        title="Career insights, industry trends & student stories"
        subtitle="Tips, guides and stories from inside India's leading German-collaborated vocational training institute."
      />

      {/* Featured post */}
      {featured && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to={`/blog/${featured.slug}`}
              className="group block bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl border border-slate-100 transition"
            >
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-80 lg:h-full overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover zoom-img" />
                  <div className="absolute top-5 left-5 flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-xs font-bold inline-flex items-center gap-1">
                      Featured
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${categoryColors[featured.category] || categoryColors.General}`}>
                      {featured.category}
                    </span>
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(featured.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{featured.read_time} min read</span>
                    <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{featured.author}</span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#0B2A6B] mb-3 group-hover:text-yellow-600 transition leading-tight">
                    {featured.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-5">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-2 text-yellow-600 font-bold group-hover:translate-x-1 transition">
                    Read article <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Filters + search */}
      <section className="pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCat("all")}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition border ${
                activeCat === "all" ? "bg-[#0B2A6B] text-white border-[#0B2A6B]" : "bg-white text-slate-700 border-slate-200 hover:border-[#0B2A6B]"
              }`}
            >
              All Posts
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                className={`px-4 py-2 text-sm font-semibold rounded-full transition border ${
                  activeCat === c ? "bg-[#0B2A6B] text-white border-[#0B2A6B]" : "bg-white text-slate-700 border-slate-200 hover:border-[#0B2A6B]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search posts..."
              className="pl-10 pr-3 py-2 text-sm rounded-full bg-white border border-slate-200 focus:outline-none focus:border-yellow-400 w-full md:w-64"
            />
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-12 text-center text-slate-500">Loading articles...</div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-slate-400">No posts match your filters.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 tilt-card animate-fade-up flex flex-col"
                  style={{ animationDelay: `${i * 0.06}s` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="absolute inset-0 w-full h-full object-cover zoom-img" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-bold ${categoryColors[p.category] || categoryColors.General}`}>
                      {p.category}
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{p.read_time} min</span>
                    </div>
                    <h3 className="font-bold text-[#0B2A6B] text-lg leading-snug mb-2 group-hover:text-yellow-600 transition">{p.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-3 mb-4 flex-1">{p.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                      <span className="inline-flex items-center gap-1.5">
                        <User className="h-3 w-3" />{p.author}
                      </span>
                      <span className="text-yellow-600 font-bold inline-flex items-center gap-1 group-hover:translate-x-1 transition">
                        Read <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: "instant" });
    api
      .get(`/blog/slug/${slug}`)
      .then((r) => {
        setPost(r.data);
        return api.get("/blog", { params: { published_only: true, category: r.data.category } });
      })
      .then((r) => setRelated((r.data || []).filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-slate-500">Loading article...</div>;
  if (!post) return (
    <div className="py-32 text-center">
      <h2 className="text-2xl font-bold text-[#0B2A6B] mb-3">Article not found</h2>
      <Link to="/blog" className="text-yellow-600 font-bold inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to blog
      </Link>
    </div>
  );

  return (
    <>
      <SEOHead title={post.title + " | Kiki Centre Blog"} description={post.excerpt} image={post.cover_image} />

      <article>
        {/* Hero with cover image */}
        <div className="relative h-[420px] lg:h-[520px] overflow-hidden bg-[#0B2A6B]">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#04102e] via-[#04102e]/70 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full text-white">
              <Link to="/blog" className="inline-flex items-center gap-1 text-yellow-300 hover:text-yellow-400 mb-5 text-sm font-semibold">
                <ArrowLeft className="h-4 w-4" /> Back to all posts
              </Link>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${categoryColors[post.category] || categoryColors.General}`}>
                  {post.category}
                </span>
                {(post.tags || []).slice(0, 3).map((t) => (
                  <span key={t} className="px-2.5 py-0.5 rounded-full bg-white/10 border border-white/20 text-xs text-slate-200">{t}</span>
                ))}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5 max-w-3xl">{post.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-200">
                <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4 text-yellow-400" />{post.author}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-yellow-400" />{new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-yellow-400" />{post.read_time} min read</span>
                {post.views > 0 && (
                  <span className="inline-flex items-center gap-1.5"><Eye className="h-4 w-4 text-yellow-400" />{post.views} views</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Article content */}
        <div className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-slate max-w-none">
              <ContentRenderer content={post.content} />
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
                <span className="text-sm font-semibold text-slate-600 inline-flex items-center gap-1"><Tag className="h-4 w-4" />Tags:</span>
                {post.tags.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-yellow-50 text-[#0B2A6B] text-xs font-semibold border border-yellow-200">
                    {t}
                  </span>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 rounded-2xl gradient-deep-blue p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-2">Ready to start your technical career?</h3>
              <p className="text-slate-300 mb-6">Apply for admission or talk to our counsellors.</p>
              <div className="flex justify-center gap-3 flex-wrap">
                <Link to="/admissions" className="px-6 py-3 rounded-lg btn-primary">Apply for Admission</Link>
                <Link to="/contact" className="px-6 py-3 rounded-lg btn-outline-light">Talk to Counsellor</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="py-16 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-[#0B2A6B] mb-8">Related articles</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 tilt-card"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img src={p.cover_image} alt={p.title} className="absolute inset-0 w-full h-full object-cover zoom-img" />
                    </div>
                    <div className="p-5">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold mb-2 ${categoryColors[p.category] || categoryColors.General}`}>
                        {p.category}
                      </span>
                      <h3 className="font-bold text-[#0B2A6B] leading-snug group-hover:text-yellow-600 transition">{p.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}

/* Lightweight markdown-ish renderer (supports #, ##, ###, ** **, paragraphs, lists) */
function ContentRenderer({ content }) {
  if (!content) return null;
  const lines = content.split("\n");
  const blocks = [];
  let current = null;
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push({ type: "ul", items: [...listItems] });
      listItems = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushList();
      if (current) { blocks.push(current); current = null; }
      continue;
    }
    if (line.startsWith("### ")) {
      flushList();
      if (current) { blocks.push(current); current = null; }
      blocks.push({ type: "h3", text: line.slice(4) });
    } else if (line.startsWith("## ")) {
      flushList();
      if (current) { blocks.push(current); current = null; }
      blocks.push({ type: "h2", text: line.slice(3) });
    } else if (line.startsWith("# ")) {
      flushList();
      if (current) { blocks.push(current); current = null; }
      blocks.push({ type: "h1", text: line.slice(2) });
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      if (current) { blocks.push(current); current = null; }
      listItems.push(line.slice(2));
    } else {
      flushList();
      if (current) {
        current.text += " " + line;
      } else {
        current = { type: "p", text: line };
      }
    }
  }
  flushList();
  if (current) blocks.push(current);

  const renderInline = (txt) => {
    // bold **text**
    const parts = txt.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={i} className="text-[#0B2A6B] font-bold">{p.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === "h1") return <h1 key={i} className="text-3xl font-bold text-[#0B2A6B] mt-8 mb-4">{renderInline(b.text)}</h1>;
        if (b.type === "h2") return <h2 key={i} className="text-2xl font-bold text-[#0B2A6B] mt-8 mb-3">{renderInline(b.text)}</h2>;
        if (b.type === "h3") return <h3 key={i} className="text-xl font-bold text-[#0B2A6B] mt-6 mb-2">{renderInline(b.text)}</h3>;
        if (b.type === "ul") return (
          <ul key={i} className="my-4 space-y-2">
            {b.items.map((it, j) => (
              <li key={j} className="flex items-start gap-3 text-slate-700 leading-relaxed">
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-500 mt-2.5 shrink-0" />
                <span>{renderInline(it)}</span>
              </li>
            ))}
          </ul>
        );
        return <p key={i} className="text-slate-700 leading-relaxed my-4 text-[15px]">{renderInline(b.text)}</p>;
      })}
    </>
  );
}
